import { useCallback, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  api,
  type EngagementParts,
  type EngagementSummary,
  type FolderListing,
  type Person,
  type TeamMember,
} from "../lib/api";
import { useAction, useResource } from "../lib/useResource";
import { AppShell, ScreenHead } from "../shell/AppShell";
import {
  Button,
  Empty,
  Failed,
  Field,
  Label,
  Select,
  SkeletonRows,
  TextArea,
} from "../components/primitives";
import { day } from "../lib/format";
import { PortalSteps, TeamSections } from "./Team";
import { BringSomeoneIn, SetupHeader } from "./Setup";
import { PartsList } from "./Engagement";
import { AccessRegister } from "./Access";

/**
 * Shaping the engagement — everything that changes it.
 *
 * **Why it is its own screen.** All of this used to sit on the engagement
 * overview, which every person on the engagement can open. The file had no
 * role check in it, so a consultant was shown the rename box, "Make it
 * active", "Add workstream", "Add milestone", "Bring someone in", "Mark it
 * ready" and "Open the portal", and the server refused every one. The user
 * put it plainly: why can a consultant add people to an engagement. They
 * could not; they were merely invited to try.
 *
 * So the controls moved here and the overview became what its name says.
 *
 * **Who gets in.** `engagement.mayManage`, sent by the server and computed
 * by `app.may_manage` — role in (Super Admin, Principal Partner, Project
 * Lead) AND reach >= 3 on this engagement. Deciding it here from the session
 * role alone would get a Project Lead wrong: they manage their own
 * engagements and not a colleague's, and the session cannot tell which is
 * which. The rail hides Setup from consultants on role alone, which is
 * enough to keep it out of the way; this check is the one that is exact.
 *
 * Someone who arrives without authority is told so in a sentence rather than
 * shown a 404. They reached a real engagement; they just do not run it.
 */
export function EngagementSetup() {
  const { id } = useParams<{ id: string }>();
  const engagementId = id ?? "";

  const engagements = useResource<EngagementSummary[]>(
    () => api.get<EngagementSummary[]>("/api/engagements"),
    [],
  );
  const parts = useResource<EngagementParts>(
    () => api.get<EngagementParts>(`/api/engagements/${engagementId}/parts`),
    [engagementId],
  );
  const team = useResource<TeamMember[]>(
    () => api.get<TeamMember[]>(`/api/engagements/${engagementId}/team`),
    [engagementId],
  );
  // Only for the "Filed" mark — the Files screen owns the folders themselves.
  const folders = useResource<FolderListing[]>(
    () => api.get<FolderListing[]>(`/api/engagements/${engagementId}/folders`),
    [engagementId],
  );
  const people = useResource<Person[]>(
    () => api.get<Person[]>("/api/people"),
    [],
  );

  const engagement =
    engagements.resource.state === "ready"
      ? engagements.resource.data.find((e) => e.id === engagementId)
      : undefined;

  const orgs = useResource<{ id: string; name: string }[]>(
    () => api.get<{ id: string; name: string }[]>("/api/organisations"),
    [],
  );
  const organisationId =
    orgs.resource.state === "ready" && engagement
      ? orgs.resource.data.find((o) => o.name === engagement.organisation)?.id
      : undefined;

  return (
    <AppShell
      engagement={
        engagement && {
          reference: engagement.reference,
          name: engagement.name,
          organisation: engagement.organisation,
        }
      }
    >
      <ScreenHead title="Setup" />

      {engagements.resource.state === "loading" && <SkeletonRows rows={4} />}
      {engagements.resource.state === "failed" && (
        <Failed
          message={engagements.resource.message}
          correlationId={engagements.resource.correlationId}
        />
      )}
      {engagements.resource.state === "ready" && !engagement && (
        <Empty title="No such engagement">
          It may have been closed, or it may not be yours.
        </Empty>
      )}

      {engagement && !engagement.mayManage && (
        <Empty
          title="Setup belongs to whoever leads this engagement"
          action={
            <Link
              to={`/engagements/${engagement.id}`}
              className="inline-flex min-h-11 items-center justify-center border border-ink bg-ink px-5 text-sm font-medium text-canvas transition-colors duration-200 hover:bg-ink/85"
            >
              Back to the engagement
            </Link>
          }
        >
          Naming it, giving it parts and putting people on it are a Lead's or
          a Partner's to do. You can see everything the engagement is on its
          overview.
        </Empty>
      )}

      {engagement && engagement.mayManage && (
        <>
          <SetupHeader
            state={{
              engagement,
              parts:
                parts.resource.state === "ready" ? parts.resource.data : undefined,
              team:
                team.resource.state === "ready" ? team.resource.data : undefined,
              folders:
                folders.resource.state === "ready"
                  ? folders.resource.data
                  : undefined,
            }}
          />

          <Details engagement={engagement} onChanged={engagements.reload} />

          {/* How it ends. Everything above shapes an engagement that is
              running; this is the only place that can stop one. */}
          <Lifecycle engagement={engagement} onChanged={engagements.reload} />

          <Parts
            engagementId={engagementId}
            parts={parts.resource.state === "ready" ? parts.resource.data : undefined}
            failed={
              parts.resource.state === "failed" ? parts.resource : undefined
            }
            onChanged={parts.reload}
          />

          {team.resource.state === "loading" && <SkeletonRows rows={3} />}
          {team.resource.state === "failed" && (
            <Failed
              message={team.resource.message}
              correlationId={team.resource.correlationId}
            />
          )}
          {team.resource.state === "ready" && (
            <>
              <TeamSections
                engagementId={engagementId}
                members={team.resource.data}
                onChanged={team.reload}
              />

              {people.resource.state === "ready" && (
                <BringSomeoneIn
                  engagementId={engagementId}
                  organisation={engagement.organisation}
                  organisationId={organisationId}
                  people={people.resource.data}
                  existing={team.resource.data}
                  workstreams={
                    parts.resource.state === "ready"
                      ? parts.resource.data.workstreams
                      : []
                  }
                  onDone={() => {
                    team.reload();
                    engagements.reload();
                  }}
                />
              )}
              {/* The override register, last on the screen because it is
                  the exception rather than the shape. Everything above
                  says what the engagement IS; this says who was let past
                  it and who was kept out, and why. Setup is already gated
                  on mayManage, which is the same authority the register's
                  own policies ask. */}
              <AccessRegister
                engagementId={engagementId}
                parts={
                  parts.resource.state === "ready"
                    ? parts.resource.data
                    : undefined
                }
                team={team.resource.data}
              />

              <PortalSteps
                engagement={engagement}
                clientCount={
                  team.resource.data.filter((m) => m.kind === "client").length
                }
                onDone={() => {
                  team.reload();
                  engagements.reload();
                }}
              />
            </>
          )}
        </>
      )}
    </AppShell>
  );
}

/** Name, brief, dates, status — the things about the engagement rather than in it. */
function Details({
  engagement,
  onChanged,
}: {
  engagement: EngagementSummary;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(engagement.name);
  const [target, setTarget] = useState(engagement.targetCompletion ?? "");
  const [brief, setBrief] = useState(engagement.brief ?? "");

  const save = useAction(async () => {
    await api.patch(`/api/engagements/${engagement.id}`, {
      name,
      targetCompletion: target || null,
      brief: brief.trim() || null,
    });
    setEditing(false);
    onChanged();
  });

  const activate = useAction(async () => {
    await api.post(`/api/engagements/${engagement.id}/activate`, {});
    onChanged();
  });

  return (
    <section className="mt-8">
      <div className="flex flex-wrap items-baseline justify-between gap-4 border-b border-line pb-4">
        <div className="min-w-0">
          <p className="font-mono text-xs text-secondary">
            {engagement.reference}
          </p>
          <p className="mt-1 text-base text-ink">
            {engagement.name}
            <span className="text-secondary"> · {engagement.organisation}</span>
          </p>
        </div>
        <Link
          to={`/engagements/${engagement.id}`}
          className="text-sm text-secondary underline-offset-4 hover:text-ink hover:underline"
        >
          See it as everyone else does
        </Link>
      </div>

      {/* The reference is not editable and says so. It is minted, permanent,
          and the whole point of the scheme is that it cannot be edited into
          something wrong. */}
      {!editing ? (
        <div className="mt-5 flex flex-wrap items-center gap-4">
          <Button tone="secondary" onClick={() => setEditing(true)}>
            {engagement.brief ? "Edit the details" : "Write the brief"}
          </Button>
          {engagement.status !== "active" && (
            <Button
              tone="primary"
              loading={activate.pending}
              onClick={() => void activate.invoke()}
            >
              Make it active
            </Button>
          )}
          {engagement.targetCompletion && (
            <span className="text-sm text-secondary">
              Target {day(engagement.targetCompletion)}
            </span>
          )}
        </div>
      ) : (
        <div className="mt-5 grid max-w-2xl gap-5 sm:grid-cols-2">
          <Field
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <Field
            label="Target completion"
            type="date"
            hint="Optional."
            value={target}
            onChange={(e) => setTarget(e.target.value)}
          />
          <div className="sm:col-span-2">
            <TextArea
              label="What this is about"
              hint="Everyone on the engagement reads this, consultants included. A name is not a brief."
              maxLength={4000}
              value={brief}
              onChange={(e) => setBrief(e.target.value)}
            />
          </div>
          <div className="flex gap-3 sm:col-span-2">
            <Button
              tone="primary"
              loading={save.pending}
              disabled={!name.trim()}
              onClick={() => void save.invoke()}
            >
              Save
            </Button>
            <Button tone="quiet" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {(save.refusal || activate.refusal) && (
        <p className="mt-4 border-l-2 border-danger pl-4 text-sm text-ink">
          {(save.refusal ?? activate.refusal)?.message}
        </p>
      )}

      <p className="mt-4 max-w-[62ch] text-sm text-secondary">
        The filing reference and client cannot be changed after the engagement
        is created. Start a new engagement if either one is wrong.
      </p>
    </section>
  );
}

/** Workstreams and milestones — "the different parts of the engagement". */
function Parts({
  engagementId,
  parts,
  failed,
  onChanged,
}: {
  engagementId: string;
  parts?: EngagementParts;
  failed?: { message: string; correlationId?: string };
  onChanged: () => void;
}) {
  const [wsName, setWsName] = useState("");
  const [msName, setMsName] = useState("");
  const [msWorkstream, setMsWorkstream] = useState("");
  const [msDue, setMsDue] = useState("");

  const addWorkstream = useAction(async () => {
    await api.post(`/api/engagements/${engagementId}/workstreams`, {
      name: wsName.trim(),
      sequence: (parts?.workstreams.length ?? 0) + 1,
    });
    setWsName("");
    onChanged();
  });

  const addMilestone = useAction(async () => {
    await api.post(`/api/engagements/${engagementId}/milestones`, {
      name: msName.trim(),
      workstreamId: msWorkstream || undefined,
      dueOn: msDue || undefined,
    });
    setMsName("");
    setMsDue("");
    onChanged();
  });

  const submitWs = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      void addWorkstream.invoke();
    },
    [addWorkstream],
  );

  return (
    <section className="mt-12">
      <Label as="h2">What this engagement is made of</Label>
      <p className="mt-2 max-w-[62ch] text-sm text-secondary">
        Workstreams divide the work; milestones are the dated points inside
        them. You can give a consultant access to one workstream instead of the
        whole engagement.
      </p>

      {failed && <Failed message={failed.message} correlationId={failed.correlationId} />}
      {!parts && !failed && <SkeletonRows rows={3} />}

      {parts && (
        <>
          {/* The same list the overview shows, from the same component, so
              what you are editing and what everyone sees cannot drift. */}
          <PartsList parts={parts} />

          <form onSubmit={submitWs} className="mt-6 flex flex-wrap items-end gap-3">
            <Field
              label="New workstream"
              value={wsName}
              onChange={(e) => setWsName(e.target.value)}
              autoComplete="off"
            />
            <Button
              tone="secondary"
              type="submit"
              loading={addWorkstream.pending}
              disabled={!wsName.trim()}
            >
              Add workstream
            </Button>
          </form>

          <div className="mt-5 flex flex-wrap items-end gap-3">
            <Field
              label="New milestone"
              value={msName}
              onChange={(e) => setMsName(e.target.value)}
              autoComplete="off"
            />
            <Select
              label="In"
              value={msWorkstream}
              onChange={(e) => setMsWorkstream(e.target.value)}
            >
              <option value="">No workstream</option>
              {parts.workstreams.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name}
                </option>
              ))}
            </Select>
            <Field
              label="Due"
              type="date"
              value={msDue}
              onChange={(e) => setMsDue(e.target.value)}
            />
            <Button
              tone="secondary"
              loading={addMilestone.pending}
              disabled={!msName.trim()}
              onClick={() => void addMilestone.invoke()}
            >
              Add milestone
            </Button>
          </div>

          {(addWorkstream.refusal || addMilestone.refusal) && (
            <p className="mt-4 border-l-2 border-danger pl-4 text-sm text-ink">
              {(addWorkstream.refusal ?? addMilestone.refusal)?.message}
            </p>
          )}
        </>
      )}
    </section>
  );
}

/**
 * How an engagement ends.
 *
 * `engagement_status` has carried five values since migration 001 and the
 * platform could only ever reach two of them. Draft became active and that
 * was the whole lifecycle: nothing could pause an engagement, nothing could
 * wind one down, and **nothing could close one**. Reported by the user as
 * "no button to close an engagement totally", which is exactly what it was.
 *
 * Archiving is not tidying and this block should not make it look like it.
 * It is the act that **ends the client's access** — `app.reach` has stopped
 * at `status <> 'archived'` since migration 003, so until this existed,
 * every client portal ever opened stayed open for ever. It also starts the
 * retention clock, which is why the confirmation says what it will say
 * rather than asking "are you sure".
 *
 * Per the prohibition in DESIGN.md, publication is the one act that earns a
 * modal. This earns the same weight by a different means: a typed
 * confirmation in place, no dialogue box, and the consequence spelled out
 * above the button rather than in it.
 */
function Lifecycle({
  engagement,
  onChanged,
}: {
  engagement: EngagementSummary;
  onChanged: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped] = useState("");
  const [retention, setRetention] = useState<string | null | undefined>(undefined);

  const hold = useAction(async () => {
    await api.post(`/api/engagements/${engagement.id}/hold`, {});
    onChanged();
  });

  const closing = useAction(async () => {
    await api.post(`/api/engagements/${engagement.id}/closing`, {});
    onChanged();
  });

  const archive = useAction(async () => {
    const result = await api.post<{ retentionUntil: string | null }>(
      `/api/engagements/${engagement.id}/archive`,
      {},
    );
    setRetention(result.retentionUntil);
    setConfirming(false);
    setTyped("");
    onChanged();
  });

  const status = engagement.status;
  const archived = status === "archived";
  const refusal = hold.refusal ?? closing.refusal ?? archive.refusal;

  return (
    <section className="mt-10 border-t border-line-strong pt-6">
      <Label as="h2">How this ends</Label>
      <p className="mt-1 max-w-[62ch] text-sm text-secondary">
        {archived
          ? "This engagement is closed. The client no longer has access, and the engagement record remains available to the firm."
          : "An engagement can be paused, wound down, or closed for good. Closing is the only one that cannot be undone."}
      </p>

      {archived ? (
        <p className="mt-5 max-w-[62ch] border-l-2 border-line pl-4 text-sm text-ink">
          Closed{retention ? `, and held until ${day(retention)}.` : "."}{" "}
          {retention
            ? "The firm's retention period applies, so nothing here can be removed before that date."
            : "No retention period is set, so nothing has a disposal date."}
        </p>
      ) : (
        <>
          <div className="mt-5 flex flex-wrap gap-3">
            {/* Paused. Offered only from the two states the server accepts,
                so a button is never shown that would be refused. */}
            {(status === "active" || status === "closing") && (
              <Button
                tone="quiet"
                loading={hold.pending}
                onClick={() => void hold.invoke()}
              >
                Put it on hold
              </Button>
            )}
            {status === "active" && (
              <Button
                tone="quiet"
                loading={closing.pending}
                onClick={() => void closing.invoke()}
              >
                Start winding it down
              </Button>
            )}
            {!confirming && (
              <Button tone="danger" onClick={() => setConfirming(true)}>
                Close it for good
              </Button>
            )}
          </div>

          {confirming && (
            <div className="mt-5 max-w-2xl border-l-2 border-danger pl-4">
              <p className="text-base text-ink">
                Closing {engagement.name} ends it. The client loses access to
                their portal immediately — they will not be told, they will
                simply find it gone — and the engagement cannot be reopened.
              </p>
              <p className="mt-2 text-sm text-secondary">
                Everything written stays on the record. If the firm has set a
                retention period, the clock on it starts today.
              </p>

              <div className="mt-4 flex flex-wrap items-end gap-3">
                <Field
                  label={`Type the reference to confirm`}
                  hint={engagement.reference}
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                />
                <Button
                  tone="danger"
                  // The reference rather than a word like "CLOSE": it is on
                  // the screen above, it is specific to THIS engagement, and
                  // it cannot be typed by muscle memory from the last time.
                  disabled={typed.trim() !== engagement.reference}
                  loading={archive.pending}
                  onClick={() => void archive.invoke()}
                >
                  Close this engagement
                </Button>
                <Button
                  tone="quiet"
                  onClick={() => {
                    setConfirming(false);
                    setTyped("");
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {refusal && (
        <p className="mt-4 max-w-[62ch] border-l-2 border-danger pl-4 text-sm text-ink">
          {refusal.message}
        </p>
      )}
    </section>
  );
}
